/* VastraGharr Luxe - shop the look (v3)
 * <vg-shop-the-look> pairs each hotspot with its product card:
 * - desktop: hover / focus a marker to highlight its card (and see a price tag), click to pin it
 * - phones: tap a marker for an in-image product card; swiping the product row lights up the matching marker
 * - arrow keys move between markers, Esc closes the open card, looks swipe / page when there are several
 * - Add buttons show "Added" for 1.6s after Dawn's product form (or the quick add window) adds the item
 * Works with theme editor section reloads and block selection.
 */
(function () {
  if (customElements.get('vg-shop-the-look')) return;

  var desktopMq = window.matchMedia('(min-width: 990px)');
  var ADDED_MS = 1600;

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  class VgShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.looks = toArray(this.querySelectorAll('[data-stl-look]'));
      if (!this.looks.length) return;
      this.ready = true;

      this.index = 0;
      this.looks.forEach(function (look, i) {
        if (look.classList.contains('is-active')) this.index = i;
      }, this);

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onMqChange = this.onMqChange.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      if (desktopMq.addEventListener) {
        desktopMq.addEventListener('change', this.onMqChange);
      } else if (desktopMq.addListener) {
        desktopMq.addListener(this.onMqChange);
      }

      /* Dawn's pubsub.js / constants.js declare these as script globals (not window properties). */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }

      /* Per-look listeners stay on the elements across a disconnect (the theme editor moves sections on reorder). */
      if (!this.looksBound) {
        this.looksBound = true;
        this.looks.forEach(this.bindLook, this);
      }
      this.observeSliders();
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.ready = false;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (desktopMq.removeEventListener) {
        desktopMq.removeEventListener('change', this.onMqChange);
      } else if (desktopMq.removeListener) {
        desktopMq.removeListener(this.onMqChange);
      }
      if (this.unsubscribeCart) {
        this.unsubscribeCart();
        this.unsubscribeCart = null;
      }
      if (this.sliderObservers) {
        this.sliderObservers.forEach(function (io) {
          io.disconnect();
        });
        this.sliderObservers = null;
      }
      (this.addedTimers || []).forEach(clearTimeout);
      this.addedTimers = [];
    }

    /* Hover / focus pairing between hotspots and mini cards */
    bindLook(look) {
      var self = this;
      var pairs = look.querySelectorAll('[data-stl-spot], [data-stl-item]');

      Array.prototype.forEach.call(pairs, function (el) {
        var key = el.getAttribute('data-stl-spot') || el.getAttribute('data-stl-item');
        el.addEventListener('mouseenter', function () {
          if (desktopMq.matches) self.highlight(look, key);
        });
        el.addEventListener('mouseleave', function () {
          if (desktopMq.matches) self.highlight(look, look.stlPinned || null);
        });
        el.addEventListener('focusin', function () {
          if (desktopMq.matches) self.highlight(look, key);
        });
        el.addEventListener('focusout', function (event) {
          if (!desktopMq.matches) return;
          if (event.relatedTarget && el.contains(event.relatedTarget)) return;
          self.highlight(look, look.stlPinned || null);
        });
      });

      /* Swipe between looks on the image (touch devices) */
      if (this.looks.length > 1) {
        var frame = look.querySelector('[data-stl-frame]');
        if (!frame) return;
        var startX = null;
        var startY = null;
        frame.addEventListener(
          'touchstart',
          function (event) {
            var t = event.changedTouches[0];
            startX = t.clientX;
            startY = t.clientY;
          },
          { passive: true }
        );
        frame.addEventListener(
          'touchend',
          function (event) {
            if (startX === null) return;
            var t = event.changedTouches[0];
            var dx = t.clientX - startX;
            var dy = t.clientY - startY;
            startX = startY = null;
            if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
            if (event.target.closest && event.target.closest('[data-stl-pop]')) return;
            self.go(dx < 0 ? self.index + 1 : self.index - 1);
          },
          { passive: true }
        );
      }
    }

    /* Phones: the product row is a swipe slider; the card most in view lights up its marker. */
    observeSliders() {
      if (!('IntersectionObserver' in window) || this.sliderObservers) return;
      var self = this;
      this.sliderObservers = [];
      this.looks.forEach(function (look) {
        var track = look.querySelector('[data-vg-slider-track]');
        var items = look.querySelectorAll('[data-stl-item]');
        if (!track || items.length < 2) return;
        var ratios = {};
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              ratios[entry.target.getAttribute('data-stl-item')] = entry.intersectionRatio;
            });
            if (desktopMq.matches || look.stlOpenKey) return;
            var best = null;
            var bestRatio = 0.55;
            Object.keys(ratios).forEach(function (key) {
              if (ratios[key] > bestRatio) {
                bestRatio = ratios[key];
                best = key;
              }
            });
            self.markCurrent(look, best);
          },
          { root: track, threshold: [0, 0.55, 0.8, 1] }
        );
        Array.prototype.forEach.call(items, function (item) {
          io.observe(item);
        });
        self.sliderObservers.push(io);
      });
    }

    markCurrent(look, key) {
      Array.prototype.forEach.call(look.querySelectorAll('[data-stl-spot]'), function (spot) {
        spot.classList.toggle('is-current', key !== null && spot.getAttribute('data-stl-spot') === String(key));
      });
    }

    highlight(look, key) {
      Array.prototype.forEach.call(look.querySelectorAll('[data-stl-spot]'), function (spot) {
        spot.classList.toggle('is-active', key !== null && spot.getAttribute('data-stl-spot') === String(key));
      });
      Array.prototype.forEach.call(look.querySelectorAll('[data-stl-item]'), function (item) {
        item.classList.toggle('is-highlighted', key !== null && item.getAttribute('data-stl-item') === String(key));
      });
    }

    onClick(event) {
      var target = event.target;
      if (!target.closest) return;
      var look = target.closest('[data-stl-look]');

      if (target.closest('[data-stl-prev]')) {
        this.go(this.index - 1, true);
        return;
      }
      if (target.closest('[data-stl-next]')) {
        this.go(this.index + 1, true);
        return;
      }
      if (!look) return;

      var spot = target.closest('[data-stl-spot]');
      if (spot) {
        this.onSpot(look, spot.getAttribute('data-stl-spot'), event.detail === 0);
        return;
      }

      if (target.closest('[data-stl-pop-close]')) {
        this.closePop(look, true);
        return;
      }

      var proxy = target.closest('[data-stl-proxy]');
      if (proxy) {
        var item = look.querySelector('[data-stl-item="' + proxy.getAttribute('data-stl-proxy') + '"]');
        var real = item && item.querySelector('[data-stl-add]');
        if (real && real.getAttribute('aria-disabled') !== 'true') {
          this.closePop(look, false);
          real.click();
        }
      }
    }

    onSpot(look, key, fromKeyboard) {
      if (desktopMq.matches) {
        look.stlPinned = look.stlPinned === key ? null : key;
        this.highlight(look, look.stlPinned || key);
        if (fromKeyboard) {
          var link = look.querySelector('[data-stl-item="' + key + '"] .vg-stl__title');
          if (link) link.focus({ preventScroll: false });
        }
        return;
      }

      if (look.stlOpenKey === key) {
        this.closePop(look, false);
        return;
      }
      this.openPop(look, key, fromKeyboard);
    }

    openPop(look, key, focusInside) {
      this.closePop(look, false);
      var pop = look.querySelector('[data-stl-pop="' + key + '"]');
      var spot = look.querySelector('[data-stl-spot="' + key + '"]');
      if (!pop || !spot) return;
      pop.hidden = false;
      spot.setAttribute('aria-expanded', 'true');
      look.stlOpenKey = key;
      look.classList.add('has-open-pop');
      this.highlight(look, key);
      this.markCurrent(look, key);
      this.scrollToItem(look, key);
      if (focusInside) {
        var title = pop.querySelector('.vg-stl__pop-title');
        if (title) title.focus({ preventScroll: true });
      }
    }

    closePop(look, restoreFocus) {
      if (!look || !look.stlOpenKey) return;
      var key = look.stlOpenKey;
      var pop = look.querySelector('[data-stl-pop="' + key + '"]');
      var spot = look.querySelector('[data-stl-spot="' + key + '"]');
      if (pop) pop.hidden = true;
      if (spot) {
        spot.setAttribute('aria-expanded', 'false');
        if (restoreFocus) spot.focus({ preventScroll: true });
      }
      look.stlOpenKey = null;
      look.classList.remove('has-open-pop');
      this.highlight(look, null);
    }

    scrollToItem(look, key) {
      var track = look.querySelector('[data-vg-slider-track]');
      var item = look.querySelector('[data-stl-item="' + key + '"]');
      if (!track || !item || track.scrollWidth <= track.clientWidth + 1) return;
      var trackRect = track.getBoundingClientRect();
      var itemRect = item.getBoundingClientRect();
      var padding = parseFloat(window.getComputedStyle(track).scrollPaddingInlineStart) || 0;
      var left = track.scrollLeft + (itemRect.left - trackRect.left) - padding;
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: reduceMotion() ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft = left;
      }
    }

    go(index, keepFocus) {
      var total = this.looks.length;
      if (total < 2) return;
      var next = ((index % total) + total) % total;
      if (next === this.index) return;

      var current = this.looks[this.index];
      var focusSelector = null;
      if (keepFocus && current.contains(document.activeElement)) {
        if (document.activeElement.hasAttribute('data-stl-prev')) focusSelector = '[data-stl-prev]';
        if (document.activeElement.hasAttribute('data-stl-next')) focusSelector = '[data-stl-next]';
      }

      this.closePop(current, false);
      current.stlPinned = null;
      this.highlight(current, null);
      this.setActive(current, false);

      var incoming = this.looks[next];
      this.setActive(incoming, true);
      this.index = next;

      if (focusSelector) {
        var target = incoming.querySelector(focusSelector);
        if (target) target.focus({ preventScroll: true });
      }

      var track = incoming.querySelector('[data-vg-slider-track]');
      if (track && !desktopMq.matches) track.scrollLeft = 0;
    }

    setActive(look, active) {
      look.classList.toggle('is-active', active);
      if (active) {
        look.removeAttribute('aria-hidden');
        look.removeAttribute('inert');
      } else {
        look.setAttribute('aria-hidden', 'true');
        look.setAttribute('inert', '');
      }
    }

    onKeydown(event) {
      var look = this.looks[this.index];
      if (event.key === 'Escape') {
        if (look && look.stlOpenKey) {
          event.preventDefault();
          this.closePop(look, true);
        }
        return;
      }

      /* Arrow keys move between the markers of the current look */
      var spot = event.target.closest && event.target.closest('[data-stl-spot]');
      if (!spot || !look) return;
      var step = 0;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
      if (event.key === 'Home') step = -Infinity;
      if (event.key === 'End') step = Infinity;
      if (!step) return;
      var spots = toArray(look.querySelectorAll('[data-stl-spot]')).filter(function (el) {
        return el.offsetParent !== null;
      });
      var at = spots.indexOf(spot);
      if (at === -1 || spots.length < 2) return;
      event.preventDefault();
      var nextIndex;
      if (step === -Infinity) nextIndex = 0;
      else if (step === Infinity) nextIndex = spots.length - 1;
      else nextIndex = (at + step + spots.length) % spots.length;
      var nextSpot = spots[nextIndex];
      nextSpot.focus({ preventScroll: true });
      if (!desktopMq.matches && look.stlOpenKey) this.openPop(look, nextSpot.getAttribute('data-stl-spot'), false);
    }

    onDocClick(event) {
      var look = this.looks[this.index];
      if (!look || !look.stlOpenKey || !event.target.closest) return;
      if (event.target.closest('[data-stl-pop]') || event.target.closest('[data-stl-spot]')) return;
      if (event.target.closest('quick-add-modal')) return;
      this.closePop(look, false);
    }

    onMqChange() {
      var self = this;
      this.looks.forEach(function (look) {
        self.closePop(look, false);
        look.stlPinned = null;
        self.highlight(look, null);
        self.markCurrent(look, null);
      });
    }

    onBlockSelect(event) {
      var i = this.looks.indexOf(event.target);
      if (i !== -1) this.go(i, false);
    }

    /* "Added" state on the button that started a successful add (direct form or quick add window). */
    onCartUpdate(event) {
      if (!event || event.source !== 'product-form' || event.productVariantId == null) return;
      var variantId = String(event.productVariantId);
      var self = this;

      toArray(this.querySelectorAll('form input[name="id"]')).forEach(function (input) {
        if (String(input.value) !== variantId || !input.form) return;
        self.markAdded(input.form.querySelector('[data-stl-add]'));
      });

      toArray(this.querySelectorAll('modal-opener[data-modal]')).forEach(function (opener) {
        var selector = opener.getAttribute('data-modal');
        var modal = null;
        try {
          modal = selector ? document.querySelector(selector) : null;
        } catch (e) {
          modal = null;
        }
        if (!modal || !modal.hasAttribute('open')) return;
        var input = modal.querySelector('form input[name="id"]');
        if (input && String(input.value) === variantId) self.markAdded(opener.querySelector('[data-stl-add]'));
      });
    }

    markAdded(button) {
      if (!button) return;
      var self = this;
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      clearTimeout(button.vgAddedTimer);
      button.vgAddedTimer = setTimeout(function () {
        button.classList.remove('is-added');
      }, ADDED_MS);
      this.addedTimers = this.addedTimers || [];
      this.addedTimers.push(button.vgAddedTimer);
      /* mirror the state on the in-image card's proxy button */
      var item = button.closest('[data-stl-item]');
      var look = button.closest('[data-stl-look]');
      if (!item || !look) return;
      var proxy = look.querySelector('[data-stl-proxy="' + item.getAttribute('data-stl-item') + '"]');
      if (proxy && proxy !== button) {
        proxy.classList.add('is-added');
        setTimeout(function () {
          proxy.classList.remove('is-added');
        }, ADDED_MS);
      }
      if (self.looks.length && look.stlOpenKey) self.closePop(look, false);
    }
  }

  customElements.define('vg-shop-the-look', VgShopTheLook);
})();
